import { isNumber, toInstant } from './piFormat.js';

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const XLSX_TYPES = ['text', 'int', 'decimal', 'percent', 'date', 'datetime'];

export const XLSX_STYLES = {
  text: 0,
  header: 1,
  int: 2,
  decimal: 3,
  percent: 4,
  date: 5,
  datetime: 6,
  guarded: 7,
  wrap: 8,
  wrapGuarded: 9,
  decimal2: 10,
  money: 11,
};

export const MAX_COLUMN_WIDTH = 60;

export const MIN_COLUMN_WIDTH = 8;

export const MAX_SHEET_NAME = 31;

export const MAX_CELL_TEXT = 32767;

export const BRASILIA_OFFSET_MINUTES = -180;

export const MIN_EXCEL_YEAR = 1900;

export const FORMULA_PREFIX = /^[=+\-@\t\r]/;

const DAY_MS = 86400000;
const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;
const ZONED = /[zZ]$|[+-]\d{2}:?\d{2}$/;
const INVALID_XML = /[\u0000-\u0008\u000b\u000c\u000e-\u001f￾￿]/g;
const SURROGATES = /[\ud800-\udbff][\udc00-\udfff]|[\ud800-\udfff]/g;
const SHEET_NAME_INVALID = /[\[\]:*?\/\\]/g;
const NUMERIC_TYPES = ['int', 'decimal', 'percent'];
const DATE_TYPES = ['date', 'datetime'];

const MAIN_NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const REL_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const PKG_REL_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';
const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

const STYLES_XML = XML_HEAD
  + '<styleSheet xmlns="' + MAIN_NS + '">'
  + '<numFmts count="6">'
  + '<numFmt numFmtId="164" formatCode="#,##0.0"/>'
  + '<numFmt numFmtId="165" formatCode="0.0%"/>'
  + '<numFmt numFmtId="166" formatCode="dd/mm/yyyy"/>'
  + '<numFmt numFmtId="167" formatCode="dd/mm/yyyy hh:mm"/>'
  + '<numFmt numFmtId="168" formatCode="#,##0.00"/>'
  + '<numFmt numFmtId="169" formatCode="#,##0.00####"/>'
  + '</numFmts>'
  + '<fonts count="2">'
  + '<font><sz val="11"/><name val="Calibri"/><family val="2"/></font>'
  + '<font><b/><sz val="11"/><name val="Calibri"/><family val="2"/></font>'
  + '</fonts>'
  + '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>'
  + '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>'
  + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
  + '<cellXfs count="12">'
  + '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'
  + '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>'
  + '<xf numFmtId="3" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '<xf numFmtId="166" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '<xf numFmtId="167" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" quotePrefix="1"/>'
  + '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>'
  + '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" quotePrefix="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>'
  + '<xf numFmtId="168" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '<xf numFmtId="169" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>'
  + '</cellXfs>'
  + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
  + '</styleSheet>';

const ROOT_RELS_XML = XML_HEAD
  + '<Relationships xmlns="' + PKG_REL_NS + '">'
  + '<Relationship Id="rId1" Type="' + REL_NS + '/officeDocument" Target="xl/workbook.xml"/>'
  + '</Relationships>';

let crcTable = null;

const crcTableOf = () => {
  if (crcTable) return crcTable;
  crcTable = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    crcTable[n] = c >>> 0;
  }
  return crcTable;
};

export const crc32 = (bytes) => {
  const table = crcTableOf();
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = table[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
};

const codePointAt = (text, i) => {
  const code = text.charCodeAt(i);
  if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
    const next = text.charCodeAt(i + 1);
    if (next >= 0xdc00 && next <= 0xdfff) return { code: 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00), size: 2 };
  }
  if (code >= 0xd800 && code <= 0xdfff) return { code: 0xfffd, size: 1 };
  return { code: code, size: 1 };
};

const utf8Length = (text) => {
  let length = 0;
  for (let i = 0; i < text.length;) {
    const point = codePointAt(text, i);
    if (point.code < 0x80) length += 1;
    else if (point.code < 0x800) length += 2;
    else if (point.code < 0x10000) length += 3;
    else length += 4;
    i += point.size;
  }
  return length;
};

export const manualUtf8 = (text) => {
  const bytes = new Uint8Array(utf8Length(text));
  let at = 0;
  for (let i = 0; i < text.length;) {
    const point = codePointAt(text, i);
    const code = point.code;
    i += point.size;
    if (code < 0x80) {
      bytes[at++] = code;
    } else if (code < 0x800) {
      bytes[at++] = 0xc0 | (code >> 6);
      bytes[at++] = 0x80 | (code & 63);
    } else if (code < 0x10000) {
      bytes[at++] = 0xe0 | (code >> 12);
      bytes[at++] = 0x80 | ((code >> 6) & 63);
      bytes[at++] = 0x80 | (code & 63);
    } else {
      bytes[at++] = 0xf0 | (code >> 18);
      bytes[at++] = 0x80 | ((code >> 12) & 63);
      bytes[at++] = 0x80 | ((code >> 6) & 63);
      bytes[at++] = 0x80 | (code & 63);
    }
  }
  return bytes;
};

export const utf8 = (text) => (typeof TextEncoder === 'function' ? new TextEncoder().encode(text) : manualUtf8(text));

const cleanText = (value) => String(value)
  .replace(SURROGATES, (match) => (match.length === 2 ? match : '�'))
  .replace(INVALID_XML, '');

export const escapeXml = (value) => cleanText(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

export const columnName = (index) => {
  let number = Math.floor(index) + 1;
  let name = '';
  while (number > 0) {
    const rest = (number - 1) % 26;
    name = String.fromCharCode(65 + rest) + name;
    number = Math.floor((number - 1) / 26);
  }
  return name;
};

const pad2 = (value) => (value < 10 ? '0' : '') + value;

const brasiliaMillis = (instant) => instant.getTime() + BRASILIA_OFFSET_MINUTES * 60000;

const brasiliaYear = (instant) => new Date(brasiliaMillis(instant)).getUTCFullYear();

const textBeforeExcel = (value) => {
  if (typeof value !== 'string') return false;
  const match = DATE_PREFIX.exec(value.trim());
  return !!match && Number(match[1]) < MIN_EXCEL_YEAR;
};

const utcDay = (parts) => {
  const date = new Date(0);
  date.setUTCFullYear(parts.year, parts.month - 1, parts.day);
  return date;
};

const dayParts = (value) => {
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    const shifted = new Date(brasiliaMillis(value));
    return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1, day: shifted.getUTCDate() };
  }
  if (typeof value !== 'string') return null;
  const text = value.trim();
  const match = DATE_PREFIX.exec(text);
  if (match && (DATE_ONLY.test(text) || !ZONED.test(text))) {
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
  }
  const instant = toInstant(text);
  return instant ? dayParts(instant) : null;
};

const validDay = (parts) => {
  if (!parts || parts.year < MIN_EXCEL_YEAR || parts.month < 1 || parts.month > 12 || parts.day < 1 || parts.day > 31) return false;
  const check = utcDay(parts);
  return check.getUTCMonth() === parts.month - 1 && check.getUTCDate() === parts.day;
};

export const excelSerial = (value, type) => {
  if (isNumber(value)) return value >= 1 ? value : null;
  if (textBeforeExcel(value)) return null;
  if (type === 'datetime') {
    const instant = typeof value === 'string' && DATE_ONLY.test(value.trim()) ? null : toInstant(value);
    if (instant) return brasiliaYear(instant) < MIN_EXCEL_YEAR ? null : (brasiliaMillis(instant) - EXCEL_EPOCH) / DAY_MS;
  }
  const parts = dayParts(value);
  if (!validDay(parts)) return null;
  return (utcDay(parts).getTime() - EXCEL_EPOCH) / DAY_MS;
};

export const brasiliaText = (value, withTime) => {
  if (textBeforeExcel(value)) return '';
  const dateOnly = typeof value === 'string' && DATE_ONLY.test(value.trim());
  if (!withTime || dateOnly) {
    const parts = dayParts(value);
    if (!validDay(parts)) return '';
    return pad2(parts.day) + '/' + pad2(parts.month) + '/' + parts.year + (withTime ? ' 00:00' : '');
  }
  const instant = toInstant(value);
  if (!instant || brasiliaYear(instant) < MIN_EXCEL_YEAR) return '';
  const shifted = new Date(brasiliaMillis(instant));
  return pad2(shifted.getUTCDate()) + '/' + pad2(shifted.getUTCMonth() + 1) + '/' + shifted.getUTCFullYear()
    + ' ' + pad2(shifted.getUTCHours()) + ':' + pad2(shifted.getUTCMinutes());
};

const fitUnits = (text, max) => {
  if (text.length <= max) return text;
  const last = text.charCodeAt(max - 1);
  return text.substring(0, last >= 0xd800 && last <= 0xdbff ? max - 1 : max);
};

export const sheetNames = (names) => {
  const used = [];
  return (Array.isArray(names) ? names : []).map((raw) => {
    const base = cleanText(raw === null || raw === undefined ? '' : raw)
      .replace(SHEET_NAME_INVALID, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^'+|'+$/g, '')
      .trim() || 'Planilha';
    let name = fitUnits(base, MAX_SHEET_NAME).trim();
    let counter = 2;
    while (used.indexOf(name.toLowerCase()) !== -1) {
      const suffix = ' (' + counter + ')';
      name = fitUnits(base, MAX_SHEET_NAME - suffix.length).trim() + suffix;
      counter += 1;
    }
    used.push(name.toLowerCase());
    return name;
  });
};

const cellParts = (cell, column) => {
  if (cell && typeof cell === 'object' && !(cell instanceof Date) && Object.prototype.hasOwnProperty.call(cell, 'value')) {
    return { value: cell.value, type: XLSX_TYPES.indexOf(cell.type) !== -1 ? cell.type : column.type };
  }
  return { value: cell, type: column.type };
};

const textOfValue = (value) => {
  if (value === true) return 'Sim';
  if (value === false) return 'Não';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

const groupedLength = (number) => {
  const whole = String(Math.floor(Math.abs(number)));
  return whole.length + Math.floor((whole.length - 1) / 3) + (number < 0 ? 1 : 0);
};

const MONEY_DECIMALS = 6;

const numberStyle = (type, decimals) => {
  if (type !== 'decimal' || decimals === null) return XLSX_STYLES[type];
  if (decimals === 0) return XLSX_STYLES.int;
  if (decimals === 2) return XLSX_STYLES.decimal2;
  if (decimals > 2) return XLSX_STYLES.money;
  return XLSX_STYLES.decimal;
};

const decimalPlaces = (style) => {
  if (style === XLSX_STYLES.money) return MONEY_DECIMALS;
  if (style === XLSX_STYLES.decimal2) return 2;
  return 1;
};

const displayLength = (value, type, style) => {
  if (type === 'int' || style === XLSX_STYLES.int) return groupedLength(value);
  if (type === 'decimal') return groupedLength(value) + 1 + decimalPlaces(style);
  if (type === 'percent') return groupedLength(value * 100) + 3;
  if (type === 'date') return 10;
  if (type === 'datetime') return 16;
  return 0;
};

const longestLine = (text) => text.split(/\r?\n/).reduce((max, line) => Math.max(max, line.length), 0);

const textCell = (ref, text, wrap) => {
  const written = cleanText(text.length > MAX_CELL_TEXT ? text.substring(0, MAX_CELL_TEXT) : text);
  const guarded = FORMULA_PREFIX.test(written);
  let style = wrap ? XLSX_STYLES.wrap : XLSX_STYLES.text;
  if (guarded) style = wrap ? XLSX_STYLES.wrapGuarded : XLSX_STYLES.guarded;
  const space = /^\s|\s$|\n/.test(written) ? ' xml:space="preserve"' : '';
  return {
    xml: '<c r="' + ref + '" t="inlineStr"' + (style ? ' s="' + style + '"' : '') + '><is><t' + space + '>' + escapeXml(written) + '</t></is></c>',
    length: longestLine(written),
  };
};

const numberCell = (ref, number, type, decimals) => {
  const style = numberStyle(type, isNumber(decimals) ? decimals : null);
  return {
    xml: '<c r="' + ref + '" s="' + style + '" t="n"><v>' + String(number) + '</v></c>',
    length: displayLength(number, type, style),
  };
};

const writeCell = (ref, cell, column) => {
  const parts = cellParts(cell, column);
  const value = parts.value;
  if (value === null || value === undefined || value === '') return null;
  if (NUMERIC_TYPES.indexOf(parts.type) !== -1) {
    if (isNumber(value)) return numberCell(ref, value, parts.type, column.decimals);
    if (typeof value === 'number') return null;
    return textCell(ref, textOfValue(value), column.wrap === true);
  }
  if (DATE_TYPES.indexOf(parts.type) !== -1) {
    const serial = excelSerial(value, parts.type);
    if (serial !== null) return numberCell(ref, serial, parts.type);
    if (typeof value === 'number' || value instanceof Date) return null;
    return textCell(ref, textOfValue(value), column.wrap === true);
  }
  if (typeof value === 'number' && !isNumber(value)) return null;
  return textCell(ref, textOfValue(value), column.wrap === true);
};

const normalizeColumns = (columns) => (Array.isArray(columns) ? columns : []).map((column) => {
  const source = column && typeof column === 'object' ? column : {};
  return {
    label: source.label === null || source.label === undefined ? '' : String(source.label),
    type: XLSX_TYPES.indexOf(source.type) !== -1 ? source.type : 'text',
    width: isNumber(source.width) && source.width > 0 ? source.width : null,
    wrap: source.wrap === true,
    decimals: isNumber(source.decimals) && source.decimals >= 0 ? Math.floor(source.decimals) : null,
  };
});

const widthOf = (column, longest) => {
  if (column.width) return Math.min(MAX_COLUMN_WIDTH, column.width);
  return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, longest + 2));
};

const sheetXml = (sheet, selected) => {
  const columns = sheet.columns;
  const rows = sheet.rows;
  const lastColumn = columnName(Math.max(columns.length, 1) - 1);
  const lastRow = rows.length + 1;
  const longest = columns.map((column) => longestLine(column.label));
  const body = [];
  const header = columns.map((column, index) => {
    const ref = columnName(index) + '1';
    return '<c r="' + ref + '" t="inlineStr" s="' + XLSX_STYLES.header + '"><is><t>' + escapeXml(column.label) + '</t></is></c>';
  });
  body.push('<row r="1">' + header.join('') + '</row>');
  rows.forEach((row, rowIndex) => {
    const number = rowIndex + 2;
    const cells = [];
    const values = Array.isArray(row) ? row : [];
    columns.forEach((column, index) => {
      const cell = writeCell(columnName(index) + number, values[index], column);
      if (!cell) return;
      cells.push(cell.xml);
      if (cell.length > longest[index]) longest[index] = cell.length;
    });
    body.push('<row r="' + number + '">' + cells.join('') + '</row>');
  });
  const cols = columns.map((column, index) => '<col min="' + (index + 1) + '" max="' + (index + 1) + '" width="'
    + widthOf(column, longest[index]) + '" customWidth="1"/>');
  const view = sheet.freeze
    ? '<sheetView workbookViewId="0"' + (selected ? ' tabSelected="1"' : '') + '>'
      + '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>'
      + '<selection pane="bottomLeft" activeCell="A2" sqref="A2"/></sheetView>'
    : '<sheetView workbookViewId="0"' + (selected ? ' tabSelected="1"' : '') + '/>';
  return XML_HEAD
    + '<worksheet xmlns="' + MAIN_NS + '" xmlns:r="' + REL_NS + '">'
    + '<dimension ref="A1:' + lastColumn + lastRow + '"/>'
    + '<sheetViews>' + view + '</sheetViews>'
    + '<sheetFormatPr defaultRowHeight="15"/>'
    + (cols.length ? '<cols>' + cols.join('') + '</cols>' : '')
    + '<sheetData>' + body.join('') + '</sheetData>'
    + (sheet.autofilter && columns.length ? '<autoFilter ref="A1:' + lastColumn + lastRow + '"/>' : '')
    + '</worksheet>';
};

const quotedSheet = (name) => "'" + name.replace(/'/g, "''") + "'";

const workbookXml = (sheets, active) => {
  const entries = sheets.map((sheet, index) => '<sheet name="' + escapeXml(sheet.name) + '" sheetId="' + (index + 1) + '" r:id="rId' + (index + 1) + '"/>');
  const filters = [];
  sheets.forEach((sheet, index) => {
    if (!sheet.autofilter || !sheet.columns.length) return;
    const range = '$A$1:$' + columnName(sheet.columns.length - 1) + '$' + (sheet.rows.length + 1);
    filters.push('<definedName name="_xlnm._FilterDatabase" localSheetId="' + index + '" hidden="1">'
      + escapeXml(quotedSheet(sheet.name) + '!' + range) + '</definedName>');
  });
  return XML_HEAD
    + '<workbook xmlns="' + MAIN_NS + '" xmlns:r="' + REL_NS + '">'
    + '<bookViews><workbookView activeTab="' + active + '"/></bookViews>'
    + '<sheets>' + entries.join('') + '</sheets>'
    + (filters.length ? '<definedNames>' + filters.join('') + '</definedNames>' : '')
    + '</workbook>';
};

const workbookRelsXml = (count) => {
  const rels = [];
  for (let i = 1; i <= count; i++) {
    rels.push('<Relationship Id="rId' + i + '" Type="' + REL_NS + '/worksheet" Target="worksheets/sheet' + i + '.xml"/>');
  }
  rels.push('<Relationship Id="rId' + (count + 1) + '" Type="' + REL_NS + '/styles" Target="styles.xml"/>');
  return XML_HEAD + '<Relationships xmlns="' + PKG_REL_NS + '">' + rels.join('') + '</Relationships>';
};

const contentTypesXml = (count) => {
  const sheets = [];
  for (let i = 1; i <= count; i++) {
    sheets.push('<Override PartName="/xl/worksheets/sheet' + i + '.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>');
  }
  return XML_HEAD
    + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
    + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
    + '<Default Extension="xml" ContentType="application/xml"/>'
    + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
    + sheets.join('')
    + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
    + '</Types>';
};

const dosStamp = (now) => {
  const date = now instanceof Date && !isNaN(now.getTime()) ? now : new Date();
  const year = Math.min(Math.max(date.getFullYear(), 1980), 2107);
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
};

export const zipStored = (files, now) => {
  const stamp = dosStamp(now);
  const entries = files.map((file) => {
    const name = utf8(file.name);
    const data = typeof file.data === 'string' ? utf8(file.data) : file.data;
    return { name: name, data: data, crc: crc32(data) };
  });
  let size = 22;
  entries.forEach((entry) => {
    size += 30 + entry.name.length + entry.data.length + 46 + entry.name.length;
  });
  const bytes = new Uint8Array(size);
  const view = new DataView(bytes.buffer);
  let at = 0;
  entries.forEach((entry) => {
    entry.offset = at;
    view.setUint32(at, 0x04034b50, true);
    view.setUint16(at + 4, 20, true);
    view.setUint16(at + 6, 0x0800, true);
    view.setUint16(at + 8, 0, true);
    view.setUint16(at + 10, stamp.time, true);
    view.setUint16(at + 12, stamp.date, true);
    view.setUint32(at + 14, entry.crc, true);
    view.setUint32(at + 18, entry.data.length, true);
    view.setUint32(at + 22, entry.data.length, true);
    view.setUint16(at + 26, entry.name.length, true);
    view.setUint16(at + 28, 0, true);
    bytes.set(entry.name, at + 30);
    bytes.set(entry.data, at + 30 + entry.name.length);
    at += 30 + entry.name.length + entry.data.length;
  });
  const directory = at;
  entries.forEach((entry) => {
    view.setUint32(at, 0x02014b50, true);
    view.setUint16(at + 4, 20, true);
    view.setUint16(at + 6, 20, true);
    view.setUint16(at + 8, 0x0800, true);
    view.setUint16(at + 10, 0, true);
    view.setUint16(at + 12, stamp.time, true);
    view.setUint16(at + 14, stamp.date, true);
    view.setUint32(at + 16, entry.crc, true);
    view.setUint32(at + 20, entry.data.length, true);
    view.setUint32(at + 24, entry.data.length, true);
    view.setUint16(at + 28, entry.name.length, true);
    view.setUint16(at + 30, 0, true);
    view.setUint16(at + 32, 0, true);
    view.setUint16(at + 34, 0, true);
    view.setUint16(at + 36, 0, true);
    view.setUint32(at + 38, 0, true);
    view.setUint32(at + 42, entry.offset, true);
    bytes.set(entry.name, at + 46);
    at += 46 + entry.name.length;
  });
  view.setUint32(at, 0x06054b50, true);
  view.setUint16(at + 4, 0, true);
  view.setUint16(at + 6, 0, true);
  view.setUint16(at + 8, entries.length, true);
  view.setUint16(at + 10, entries.length, true);
  view.setUint32(at + 12, at - directory, true);
  view.setUint32(at + 16, directory, true);
  view.setUint16(at + 20, 0, true);
  return bytes;
};

const normalizeSheets = (sheets) => {
  const list = (Array.isArray(sheets) ? sheets : []).filter((sheet) => sheet && typeof sheet === 'object');
  const source = list.length ? list : [{ name: 'Planilha', columns: [], rows: [] }];
  const names = sheetNames(source.map((sheet) => sheet.name));
  return source.map((sheet, index) => ({
    name: names[index],
    columns: normalizeColumns(sheet.columns),
    rows: Array.isArray(sheet.rows) ? sheet.rows : [],
    freeze: sheet.freeze !== false,
    autofilter: sheet.autofilter !== false,
  }));
};

export const buildXlsxBytes = (sheets, options) => {
  const settings = options || {};
  const list = normalizeSheets(sheets);
  const active = isNumber(settings.activeSheet) && settings.activeSheet >= 0 && settings.activeSheet < list.length
    ? Math.floor(settings.activeSheet)
    : 0;
  const files = [
    { name: '[Content_Types].xml', data: contentTypesXml(list.length) },
    { name: '_rels/.rels', data: ROOT_RELS_XML },
    { name: 'xl/workbook.xml', data: workbookXml(list, active) },
    { name: 'xl/_rels/workbook.xml.rels', data: workbookRelsXml(list.length) },
    { name: 'xl/styles.xml', data: STYLES_XML },
  ];
  list.forEach((sheet, index) => {
    files.push({ name: 'xl/worksheets/sheet' + (index + 1) + '.xml', data: sheetXml(sheet, index === active) });
  });
  return zipStored(files, settings.now);
};

export const buildXlsx = (sheets, options) => new Blob([buildXlsxBytes(sheets, options)], { type: XLSX_MIME });



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piXlsx.js